import type { CSSProperties, ReactNode } from 'react';
import { useId } from 'react';
import {
  percentDailyValue,
  roundForLabel,
  scaleNutrients,
  type NutrientKey,
  type Nutrients,
} from '@crave/costing';
import { ALLERGEN_LABELS, isAllergen } from '@crave/shared';

/*
 * A Nutrition Facts panel in the FDA's standard format (21 CFR 101.9(d)): black on white, a
 * heavy title, thick bars between sections, hairlines between nutrients. Amounts use the label
 * rounding and %DV from @crave/costing, so what the owner sees in admin is what a guest sees.
 * Styles are hoisted once by React (style precedence), so the label looks the same in any app.
 */
const CSS = `
.crave-nf { container-type: inline-size; box-sizing: border-box; width: 100%; max-width: 330px; min-width: 0;
  background: #fff; color: #000; border: 1px solid #000; padding: 4px 7px 7px;
  font-family: 'Helvetica Neue', Helvetica, Arial, 'Liberation Sans', sans-serif; font-size: 14px; line-height: 1.25;
  text-align: left; font-variant-numeric: normal; -webkit-font-smoothing: antialiased; }
.crave-nf[data-dual='true'] { max-width: 460px; }
.crave-nf * { box-sizing: border-box; }
.crave-nf-title { margin: 0; font-size: 34px; font-size: clamp(24px, 10.4cqi, 36px); font-weight: 900; line-height: 1.05;
  letter-spacing: -0.01em; white-space: nowrap; color: #000; }
.crave-nf[data-dual='true'] .crave-nf-title { font-size: clamp(24px, 8cqi, 38px); }
.crave-nf-servings { margin: 3px 0 0; padding-top: 3px; border-top: 1px solid #000; font-size: 15px; }
.crave-nf-size { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; margin: 0;
  padding-bottom: 3px; border-bottom: 10px solid #000; font-size: 17px; font-weight: 800; }
.crave-nf-size > span:last-child { text-align: right; overflow-wrap: anywhere; }
.crave-nf table { width: 100%; border-collapse: collapse; border-spacing: 0; margin: 0; color: #000; }
.crave-nf th, .crave-nf td { padding: 2px 0 1px; border-top: 1px solid #000; font-weight: 400; vertical-align: baseline;
  text-align: left; color: #000; background: #fff; }
.crave-nf td.crave-nf-num, .crave-nf th.crave-nf-num { text-align: right; white-space: nowrap; padding-left: 6px; }
.crave-nf b { font-weight: 800; }
.crave-nf-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.crave-nf tr.crave-nf-first > * { border-top: 0; }
.crave-nf tr.crave-nf-per > * { font-size: 13px; font-weight: 800; padding-top: 3px; }
.crave-nf tr.crave-nf-cal > * { border-top: 0; font-weight: 900; line-height: 1; padding: 0 0 3px; }
.crave-nf tr.crave-nf-cal > th { font-size: 26px; font-size: clamp(22px, 8cqi, 30px); }
.crave-nf tr.crave-nf-cal > td { font-size: 38px; font-size: clamp(30px, 12cqi, 44px); }
.crave-nf[data-dual='true'] tr.crave-nf-cal > th { font-size: clamp(18px, 5.4cqi, 26px); }
.crave-nf[data-dual='true'] tr.crave-nf-cal > td { font-size: clamp(22px, 7cqi, 34px); }
.crave-nf tr.crave-nf-dvhead > * { border-top: 5px solid #000; font-size: 13px; font-weight: 800; padding-top: 3px; }
.crave-nf tr.crave-nf-colhead > * { border-top: 1px solid #000; font-size: 13px; font-weight: 800; text-align: right; }
.crave-nf tr.crave-nf-colhead > *:first-child { text-align: left; }
.crave-nf tr.crave-nf-thick > * { border-top: 10px solid #000; }
.crave-nf .crave-nf-in1 { padding-left: 1em; }
.crave-nf .crave-nf-in2 { padding-left: 2em; }
.crave-nf[data-dual='true'] th, .crave-nf[data-dual='true'] td { font-size: 13px; }
.crave-nf[data-dual='true'] tr.crave-nf-per > *, .crave-nf[data-dual='true'] tr.crave-nf-dvhead > *,
.crave-nf[data-dual='true'] tr.crave-nf-colhead > * { font-size: 12px; }
.crave-nf[data-dual='true'] td.crave-nf-num { padding-left: 4px; }
.crave-nf[data-dual='true'] .crave-nf-col2 { padding-left: 8px; border-left: 1px solid #000; }
.crave-nf-foot { margin: 0; padding-top: 4px; border-top: 5px solid #000; font-size: 11px; line-height: 1.3; }
.crave-nf-foot > span { display: inline-block; }
@container (max-width: 300px) {
  .crave-nf th, .crave-nf td { font-size: 13px; }
  .crave-nf-size { font-size: 15px; }
  .crave-nf-servings { font-size: 14px; }
}
/* A two-column label on a phone: smaller type and tighter gutters so the numbers never spill. */
@container (max-width: 380px) {
  .crave-nf[data-dual='true'] th, .crave-nf[data-dual='true'] td { font-size: 11.5px; }
  .crave-nf[data-dual='true'] tr.crave-nf-per > *, .crave-nf[data-dual='true'] tr.crave-nf-dvhead > *,
  .crave-nf[data-dual='true'] tr.crave-nf-colhead > * { font-size: 11px; }
  .crave-nf[data-dual='true'] td.crave-nf-num { padding-left: 3px; }
  .crave-nf[data-dual='true'] .crave-nf-col2 { padding-left: 4px; }
  .crave-nf[data-dual='true'] .crave-nf-in1 { padding-left: 0.6em; }
  .crave-nf[data-dual='true'] .crave-nf-in2 { padding-left: 1.2em; }
  .crave-nf[data-dual='true'] .crave-nf-size { font-size: 14px; }
}
`;

type Row = {
  key: NutrientKey;
  /** Name as printed. Bold names are the main nutrients. */
  name: ReactNode;
  bold?: boolean;
  indent?: 0 | 1 | 2;
  /** Added sugars print as "Includes 10g Added Sugars" in a single-column label. */
  includes?: boolean;
};

const MACROS: readonly Row[] = [
  { key: 'fat_g', name: 'Total Fat', bold: true },
  { key: 'saturated_fat_g', name: 'Saturated Fat', indent: 1 },
  {
    key: 'trans_fat_g',
    name: (
      <>
        <i>Trans</i> Fat
      </>
    ),
    indent: 1,
  },
  { key: 'cholesterol_mg', name: 'Cholesterol', bold: true },
  { key: 'sodium_mg', name: 'Sodium', bold: true },
  { key: 'carbohydrate_g', name: 'Total Carbohydrate', bold: true },
  { key: 'fiber_g', name: 'Dietary Fiber', indent: 1 },
  { key: 'sugars_g', name: 'Total Sugars', indent: 1 },
  { key: 'added_sugars_g', name: 'Added Sugars', indent: 2, includes: true },
  { key: 'protein_g', name: 'Protein', bold: true },
];

const MICROS: readonly Row[] = [
  { key: 'vitamin_d_mcg', name: 'Vitamin D' },
  { key: 'calcium_mg', name: 'Calcium' },
  { key: 'iron_mg', name: 'Iron' },
  { key: 'potassium_mg', name: 'Potassium' },
];

const has = (n: Nutrients | null | undefined, key: NutrientKey): n is Nutrients =>
  typeof n?.[key] === 'number' && Number.isFinite(n[key]);

/** "8g", "less than 1g" (shown as "<1g"), "160mg", the way a printed label writes amounts. */
function Amount({ nutrients, k }: { nutrients: Nutrients; k: NutrientKey }) {
  const a = roundForLabel(k, nutrients[k] ?? 0);
  const unit = k === 'calories' ? '' : k.endsWith('_mcg') ? 'mcg' : k.endsWith('_mg') ? 'mg' : 'g';
  const value = `${a.value.toLocaleString('en-US')}${unit}`;
  if (!a.lessThan) return <>{value}</>;
  return (
    <>
      <span aria-hidden="true">&lt;</span>
      <span className="crave-nf-sr">less than </span>
      {value}
    </>
  );
}

function Percent({ nutrients, k }: { nutrients: Nutrients; k: NutrientKey }) {
  const pct = percentDailyValue(k, nutrients[k] ?? 0);
  return pct === null ? null : <b>{pct}%</b>;
}

function servingsText(n: number): string {
  if (Number.isInteger(n)) return `${n} ${n === 1 ? 'serving' : 'servings'} per container`;
  const half = Math.round(n * 2) / 2;
  return `About ${half.toLocaleString('en-US')} servings per container`;
}

export interface NutritionFactsLabelProps {
  /** Unrounded amounts for one serving, keyed like @crave/costing Nutrients (calories, fat_g, ...). */
  perServing: Nutrients;
  /** Printed next to "Serving size", e.g. "1 slice (118g)". */
  servingSize: string;
  /** Printed as "8 servings per container". Omit to leave the line out. */
  servingsPerContainer?: number | null | undefined;
  /**
   * Replaces the text of the servings line, e.g. "6 slices per pizza". When given, the line is
   * printed even without servingsPerContainer.
   */
  servingsText?: string | null | undefined;
  /**
   * Adds a second column for the whole container (FDA dual-column format). Its amounts are
   * `perContainer` when given, otherwise perServing times servingsPerContainer.
   */
  showPerContainer?: boolean | undefined;
  perContainer?: Nutrients | null | undefined;
  /** Column headings for the dual-column label. */
  servingColumnLabel?: string | undefined;
  containerColumnLabel?: string | undefined;
  /** Heading level of the "Nutrition Facts" title (default 3). */
  headingLevel?: 2 | 3 | 4 | undefined;
  /** Leave out the "% Daily Value" footnote (keep it on anything a guest sees). */
  hideFootnote?: boolean | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/**
 * The FDA-style Nutrition Facts panel. Rows for nutrients missing from `perServing` are left out
 * (vitamins and minerals, added sugars), so an incomplete set never prints a made-up zero.
 */
export function NutritionFactsLabel({
  perServing,
  servingSize,
  servingsPerContainer,
  servingsText: servingsTextOverride,
  showPerContainer = false,
  perContainer,
  servingColumnLabel = 'Per serving',
  containerColumnLabel = 'Per container',
  headingLevel = 3,
  hideFootnote = false,
  className,
  style,
}: NutritionFactsLabelProps) {
  const titleId = useId();
  const Heading = `h${headingLevel}` as const;
  const servings =
    typeof servingsPerContainer === 'number' &&
    Number.isFinite(servingsPerContainer) &&
    servingsPerContainer > 0
      ? servingsPerContainer
      : null;
  const servingsLine =
    servingsTextOverride?.trim() || (servings !== null ? servingsText(servings) : null);
  const container: Nutrients | null = showPerContainer
    ? (perContainer ?? (servings !== null ? scaleNutrients(perServing, servings) : null))
    : null;
  const dual = container !== null;

  const macros = MACROS.filter((r) => has(perServing, r.key));
  const micros = MICROS.filter((r) => has(perServing, r.key));
  const cols = dual ? 5 : 2;

  const nameCell = (r: Row, withAmount: boolean) => {
    const cls = r.indent === 2 ? 'crave-nf-in2' : r.indent === 1 ? 'crave-nf-in1' : undefined;
    const label = r.bold ? <b>{r.name}</b> : r.name;
    if (!withAmount) {
      return (
        <th scope="row" className={cls}>
          {r.includes ? <>Includes {label}</> : label}
        </th>
      );
    }
    return (
      <th scope="row" className={cls}>
        {r.includes ? (
          <>
            Includes <Amount nutrients={perServing} k={r.key} /> {label}
          </>
        ) : (
          <>
            {label} <Amount nutrients={perServing} k={r.key} />
          </>
        )}
      </th>
    );
  };

  const rowFor = (r: Row, rowClass?: string) => (
    <tr key={r.key} className={rowClass}>
      {dual ? (
        <>
          {nameCell(r, false)}
          <td className="crave-nf-num">
            <Amount nutrients={perServing} k={r.key} />
          </td>
          <td className="crave-nf-num">
            <Percent nutrients={perServing} k={r.key} />
          </td>
          <td className="crave-nf-num crave-nf-col2">
            {has(container, r.key) ? <Amount nutrients={container} k={r.key} /> : null}
          </td>
          <td className="crave-nf-num">
            {has(container, r.key) ? <Percent nutrients={container} k={r.key} /> : null}
          </td>
        </>
      ) : (
        <>
          {nameCell(r, true)}
          <td className="crave-nf-num">
            <Percent nutrients={perServing} k={r.key} />
          </td>
        </>
      )}
    </tr>
  );

  return (
    <section
      className={['crave-nf', className].filter(Boolean).join(' ')}
      style={style}
      data-dual={dual ? 'true' : 'false'}
      aria-labelledby={titleId}
    >
      <style href="crave-nutrition-facts" precedence="default">
        {CSS}
      </style>
      <Heading id={titleId} className="crave-nf-title">
        Nutrition Facts
      </Heading>
      {servingsLine !== null && <p className="crave-nf-servings">{servingsLine}</p>}
      <p
        className="crave-nf-size"
        style={servingsLine === null ? { borderTop: '1px solid #000', marginTop: 3 } : undefined}
      >
        <span>Serving size</span>
        <span>{servingSize}</span>
      </p>
      <table>
        <caption className="crave-nf-sr">
          {dual
            ? `Nutrition Facts, ${servingColumnLabel.toLowerCase()} and ${containerColumnLabel.toLowerCase()}`
            : 'Nutrition Facts per serving'}
        </caption>
        {dual ? (
          <thead>
            <tr className="crave-nf-colhead crave-nf-first">
              <td style={{ borderTop: 0 }} />
              <th scope="colgroup" colSpan={2} style={{ borderTop: 0 }}>
                {servingColumnLabel}
              </th>
              <th scope="colgroup" colSpan={2} className="crave-nf-col2" style={{ borderTop: 0 }}>
                {containerColumnLabel}
              </th>
            </tr>
          </thead>
        ) : (
          <thead className="crave-nf-sr">
            <tr>
              <th scope="col">Nutrient and amount</th>
              <th scope="col">% Daily Value</th>
            </tr>
          </thead>
        )}
        <tbody>
          {!dual && (
            <tr className="crave-nf-per crave-nf-first">
              <td colSpan={cols}>Amount per serving</td>
            </tr>
          )}
          <tr className="crave-nf-cal">
            <th scope="row">Calories</th>
            {dual ? (
              <>
                <td className="crave-nf-num" colSpan={2}>
                  {has(perServing, 'calories') ? (
                    <Amount nutrients={perServing} k="calories" />
                  ) : null}
                </td>
                <td className="crave-nf-num crave-nf-col2" colSpan={2}>
                  {has(container, 'calories') ? (
                    <Amount nutrients={container} k="calories" />
                  ) : null}
                </td>
              </>
            ) : (
              <td className="crave-nf-num">
                {has(perServing, 'calories') ? (
                  <Amount nutrients={perServing} k="calories" />
                ) : null}
              </td>
            )}
          </tr>
          <tr className="crave-nf-dvhead">
            {dual ? (
              <>
                <td />
                <td className="crave-nf-num" colSpan={2}>
                  <span aria-hidden="true">% DV*</span>
                  <span className="crave-nf-sr">Percent Daily Value</span>
                </td>
                <td className="crave-nf-num crave-nf-col2" colSpan={2}>
                  <span aria-hidden="true">% DV*</span>
                  <span className="crave-nf-sr">Percent Daily Value</span>
                </td>
              </>
            ) : (
              <td className="crave-nf-num" colSpan={cols}>
                % Daily Value*
              </td>
            )}
          </tr>
          {macros.map((r) => rowFor(r))}
        </tbody>
        {micros.length > 0 && (
          <tbody>{micros.map((r, i) => rowFor(r, i === 0 ? 'crave-nf-thick' : undefined))}</tbody>
        )}
      </table>
      {!hideFootnote && (
        <p
          className="crave-nf-foot"
          style={micros.length === 0 ? { borderTopWidth: 10 } : undefined}
        >
          * The % Daily Value (DV) tells you how much a nutrient in a serving of food contributes to
          a daily diet. 2,000 calories a day is used for general nutrition advice.
        </p>
      )}
    </section>
  );
}

/** Plain names for allergen codes in the standard order: ['wheat', 'milk'] gives ['Milk', 'Wheat']. */
export function allergenLabels(codes: Iterable<string> | null | undefined): string[] {
  const present = new Set(codes ?? []);
  const known = Object.keys(ALLERGEN_LABELS).filter((a) => present.has(a));
  const unknown = [...present]
    .filter((c) => c && !isAllergen(c))
    .map((c) => {
      const words = c.replace(/_/g, ' ').trim();
      return words.charAt(0).toUpperCase() + words.slice(1);
    });
  return [...known.map((a) => ALLERGEN_LABELS[a as keyof typeof ALLERGEN_LABELS]), ...unknown];
}

/** "Milk, Wheat and Soybeans" style text, or '' for none. */
export function allergenText(codes: Iterable<string> | null | undefined): string {
  return allergenLabels(codes).join(', ');
}

export interface AllergenListProps {
  allergens: Iterable<string> | null | undefined;
  /** Bold lead-in (default "Contains:"). */
  prefix?: string | undefined;
  /** Shown when there are none (default "No major allergens"). Pass null to render nothing. */
  none?: string | null | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}

/** "Contains: Milk, Wheat" from allergen codes. */
export function AllergenList({
  allergens,
  prefix = 'Contains:',
  none = 'No major allergens',
  className,
  style,
}: AllergenListProps) {
  const labels = allergenLabels(allergens);
  if (labels.length === 0) {
    if (none === null) return null;
    return (
      <p className={className} style={{ margin: 0, ...style }}>
        {none}
      </p>
    );
  }
  return (
    <p className={className} style={{ margin: 0, ...style }}>
      <strong>{prefix}</strong> {labels.join(', ')}
    </p>
  );
}
